/* Sallora local wishlist — adapted from dlerm/shopify-wishlist (MIT).
   Stores product handles in localStorage. No app, no login.
   Wrapped in an IIFE so nothing leaks to the global scope. */
(function () {
  'use strict';

  const LOCAL_STORAGE_WISHLIST_KEY = 'sallora-wishlist';
  const LOCAL_STORAGE_DELIMITER = ',';
  const BUTTON_ACTIVE_CLASS = 'active';
  const GRID_LOADED_CLASS = 'loaded';

  const selectors = {
    button: '[button-wishlist]',
    grid: '[grid-wishlist]',
    productCard: '.card-product',
    count: '[wishlist-count]',
    countWrapper: '[wishlist-count-wrapper]',
    empty: '[wishlist-empty]',
    page: '.x-wishlist-page',
    title: '[wishlist-title]',
    actions: '[wishlist-actions]',
    share: '[wishlist-share]',
    productShare: '[product-share]',
    toast: '[wishlist-toast]',
  };

  // Remove (X) icon used on the favourites page in place of the heart.
  const REMOVE_SVG =
    '<svg class="x-wishlist-x" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M7 7l10 10M17 7L7 17"/></svg>';

  const getWishlist = () => {
    const wl = localStorage.getItem(LOCAL_STORAGE_WISHLIST_KEY) || false;
    return wl ? wl.split(LOCAL_STORAGE_DELIMITER) : [];
  };

  const setWishlist = (array) => {
    if (array.length) localStorage.setItem(LOCAL_STORAGE_WISHLIST_KEY, array.join(LOCAL_STORAGE_DELIMITER));
    else localStorage.removeItem(LOCAL_STORAGE_WISHLIST_KEY);
    document.dispatchEvent(new CustomEvent('shopify-wishlist:updated', { detail: { wishlist: array } }));
    return array;
  };

  const updateWishlist = (handle) => {
    const wishlist = getWishlist();
    const i = wishlist.indexOf(handle);
    if (i === -1) wishlist.push(handle);
    else wishlist.splice(i, 1);
    return setWishlist(wishlist);
  };

  const wishlistContains = (handle) => getWishlist().includes(handle);

  // A shared list is passed via ?wl=... — read-only view of someone else's favourites.
  // Values are product IDs (short, e.g. ?wl=123,456). Legacy links may carry handles.
  const getSharedParam = () => {
    try {
      const p = new URLSearchParams(window.location.search).get('wl');
      return p ? p.split(',').map((s) => s.trim()).filter(Boolean) : null;
    } catch (e) {
      return null;
    }
  };

  // id -> handle map, fetched once from the public storefront product feed.
  let idHandleMapPromise = null;
  const fetchIdHandleMap = () => {
    if (idHandleMapPromise) return idHandleMapPromise;
    idHandleMapPromise = fetch('/products.json?limit=250')
      .then((r) => r.json())
      .then((d) => {
        const m = {};
        (d.products || []).forEach((p) => { m[String(p.id)] = p.handle; });
        return m;
      })
      .catch(() => ({}));
    return idHandleMapPromise;
  };

  // Resolve the ?wl param to product handles (so cards can be fetched by handle).
  const resolveSharedHandles = async () => {
    const param = getSharedParam();
    if (!param) return null;
    const allNumeric = param.every((v) => /^\d+$/.test(v));
    if (!allNumeric) return param; // legacy handle-based link — use as-is
    const map = await fetchIdHandleMap();
    return param.map((id) => map[id]).filter(Boolean);
  };

  const fetchProductCardHTML = (handle) => {
    const url = `/products/${handle}?view=card`;
    return fetch(url)
      .then((res) => res.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const card = doc.querySelector(selectors.productCard);
        return card ? card.outerHTML : '';
      })
      .catch((err) => {
        console.error('[Wishlist] Failed to load card for handle:', handle, err);
        return '';
      });
  };

  // On the owner's favourites page, turn each heart toggle into an explicit remove (X).
  const convertToRemove = (grid) => {
    const page = grid.closest(selectors.page);
    const label = (page && page.dataset.removeLabel) || '';
    grid.querySelectorAll(selectors.button).forEach((b) => {
      b.classList.add('is-remove');
      b.innerHTML = REMOVE_SVG;
      if (label) {
        b.setAttribute('aria-label', label);
        b.setAttribute('title', label);
      }
    });
  };

  const setupGrid = async (grid) => {
    const isShared = getSharedParam() !== null;
    const emptyEl = document.querySelector(selectors.empty);
    const actionsEl = document.querySelector(selectors.actions);
    const titleEl = document.querySelector(selectors.title);
    const page = grid.closest(selectors.page);

    // Title reflects whether you're viewing your own list or a shared one.
    if (titleEl && page) {
      const own = page.dataset.title || titleEl.textContent;
      const sh = page.dataset.titleShared || own;
      titleEl.textContent = isShared ? sh : own;
    }

    const list = isShared ? (await resolveSharedHandles()) || [] : getWishlist();

    if (!list.length) {
      grid.innerHTML = '';
      grid.classList.add(GRID_LOADED_CLASS, 'is-empty');
      if (emptyEl) emptyEl.style.display = '';
      if (actionsEl) actionsEl.style.display = 'none';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    const responses = await Promise.all(list.map(fetchProductCardHTML));
    grid.innerHTML = responses.join('');
    grid.classList.add(GRID_LOADED_CLASS);
    grid.classList.remove('is-empty');
    grid.classList.toggle('is-shared', isShared);
    initButtons();
    mountCardSliders(grid);
    // Own list → X (remove). Shared list → keep heart (add to your own).
    if (!isShared) convertToRemove(grid);
    if (actionsEl) actionsEl.style.display = isShared ? 'none' : '';
  };

  const setupButtons = (buttons) => {
    buttons.forEach((button) => {
      if (button.dataset.wishlistBound) return;
      const handle = button.dataset.productHandle || false;
      if (!handle) return console.error('[Wishlist] Missing data-product-handle attribute.');
      if (wishlistContains(handle)) button.classList.add(BUTTON_ACTIVE_CLASS);
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateWishlist(handle);
        button.classList.toggle(BUTTON_ACTIVE_CLASS);
      });
      button.dataset.wishlistBound = '1';
    });
  };

  const initGrid = () => {
    const grid = document.querySelector(selectors.grid);
    if (grid) setupGrid(grid);
  };

  const initButtons = () => {
    const buttons = document.querySelectorAll(selectors.button);
    if (buttons.length) setupButtons(buttons);
  };

  // The per-card photo slider (swipe between images + dots) on mobile is an
  // Alpine `x-intersect` directive baked into each card. Cards injected here via
  // innerHTML are never seen by Alpine, so that slider never mounts on the
  // favourites page (06-21/06-23 regression). Re-initialise ONLY the slideshow
  // elements — NOT the whole card — so the favourites' own ATC / badges / variant
  // picker stay exactly as they are (those live on the <a> ancestor / siblings,
  // which initTree never walks up into).
  // Mobile only: on desktop (≥1024px) the cards use hover-reveal-second-image and
  // the theme's own breakpoint config destroys this slider at 1024px anyway, so
  // there is nothing to mount and desktop is left untouched.
  const MOBILE_SLIDER_MQ = '(max-width: 1023px)';
  const mountCardSliders = (grid) => {
    try {
      if (!window.matchMedia || !window.matchMedia(MOBILE_SLIDER_MQ).matches) return;
    } catch (e) { return; }
    // The slideshow div carries an `x-intersect.*` attribute (dotted modifiers),
    // so match by its stable id prefix and confirm an x-intersect attr is present.
    const sliders = [...grid.querySelectorAll('[id^="x-slideshow-card-product-"]')]
      .filter((el) => el.getAttributeNames().some((n) => n.indexOf('x-intersect') === 0));
    if (!sliders.length) return;
    let tries = 0;
    const run = () => {
      if (window.Alpine && typeof window.Alpine.initTree === 'function') {
        sliders.forEach((el) => { try { window.Alpine.initTree(el); } catch (e) {} });
      } else if (tries++ < 50) {
        setTimeout(run, 20); // Alpine not ready yet — poll briefly
      }
    };
    run();
  };

  const showToast = (msg) => {
    if (!msg) return;
    let t = document.querySelector(selectors.toast);
    if (!t) {
      // No toast on this page (e.g. product page) — create a floating one.
      t = document.createElement('div');
      t.className = 'x-wishlist-toast';
      t.setAttribute('wishlist-toast', '');
      t.setAttribute('aria-live', 'polite');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(t._timer);
    t._timer = setTimeout(() => t.classList.remove('is-visible'), 2200);
  };

  // Product-page share: copy the current product URL to the clipboard + toast.
  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        return true;
      } catch (e2) {
        return false;
      }
    }
  };

  const initProductShare = () => {
    document.querySelectorAll(selectors.productShare).forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const url = btn.dataset.shareUrl || (window.location.origin + window.location.pathname);
        const ok = await copyText(url);
        if (ok) showToast(btn.dataset.copied || 'Link');
        else window.prompt(btn.dataset.copied || 'Link', url);
      });
    });
  };

  const buildShareUrl = () => {
    const base = window.location.origin + window.location.pathname;
    // Prefer short numeric product IDs (read off the rendered cards).
    const grid = document.querySelector(selectors.grid);
    let ids = [];
    if (grid) {
      ids = [...grid.querySelectorAll(selectors.button)]
        .map((b) => b.dataset.productId)
        .filter(Boolean);
    }
    if (ids.length) return base + '?wl=' + ids.join(',');
    return base + '?wl=' + getWishlist().join(','); // fallback (handles)
  };

  const initShare = () => {
    const btn = document.querySelector(selectors.share);
    if (!btn || btn.dataset.bound) return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', async () => {
      if (!getWishlist().length) return;
      const url = buildShareUrl();
      const ok = await copyText(url);
      if (ok) showToast(btn.dataset.copied || 'Link');
      else window.prompt(btn.dataset.copied || 'Link', url);
    });
  };

  const updateCount = () => {
    const count = getWishlist().length;
    document.querySelectorAll(selectors.count).forEach((el) => { el.textContent = count; });
    document.querySelectorAll(selectors.countWrapper).forEach((el) => {
      el.classList.toggle('is-empty', count === 0);
    });
  };

  const init = () => {
    initButtons();
    initGrid();
    initShare();
    initProductShare();
    updateCount();
  };

  document.addEventListener('shopify-wishlist:updated', () => {
    updateCount();
    initGrid();
  });

  // Theme editor / dynamic section reloads
  document.addEventListener('shopify:section:load', init);

  // Bind hearts injected AFTER the first render — product recommendations
  // ("you might also like"), AJAX collection filters and quick-view are loaded
  // lazily and never fire shopify:section:load, so init() alone misses them.
  const observeDynamicButtons = () => {
    if (typeof MutationObserver === 'undefined') return;
    const start = () => {
      let queued = false;
      const flush = () => { queued = false; initButtons(); };
      new MutationObserver((mutations) => {
        if (queued) return;
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            if ((node.matches && node.matches(selectors.button)) ||
                (node.querySelector && node.querySelector(selectors.button))) {
              queued = true;
              requestAnimationFrame(flush);
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) start();
    else document.addEventListener('DOMContentLoaded', start);
  };

  // Kick off — all helpers above are defined, so this is safe under defer.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  observeDynamicButtons();
})();


/* ============================================================
   Sallora — favourites variant picker (size / material / future).
   Two jobs:
   1) When a multi-variant product is ADDED to the wishlist, force an
      option choice and store it in `sallora-wishlist-sizes`
      (handle -> {id,title,price}); the favourites panel then shows the
      exact price instead of "od ...".
   2) Expose window.SalloraWishlistPicker.openEdit(handle, onDone) so the
      favourites page "Zmień" button can re-open the same modal to change
      the chosen variant.
   Adaptive UI: 1 option -> flat list; 2+ options -> grouped selectors
   (one row per option, the combination resolved to a variant; impossible
   or sold-out combinations are disabled). This block does NOT touch the
   wishlist engine above. */
(function () {
  'use strict';

  var WL_KEY = 'sallora-wishlist';
  var SIZE_KEY = 'sallora-wishlist-sizes';
  var BTN = '[button-wishlist]';

  var EN = (document.documentElement.lang || '').toLowerCase().indexOf('en') === 0
        || window.location.pathname.indexOf('/en') === 0;
  var T = EN
    ? { sizeTitle: 'Choose size', variantTitle: 'Choose option', optsTitle: 'Choose options',
        subAdd: 'Pick an option to add it to favourites', subEdit: 'Update your choice',
        add: 'Add to favourites', save: 'Save', sold: 'Sold out', unavail: 'This combination is unavailable' }
    : { sizeTitle: 'Wybierz rozmiar', variantTitle: 'Wybierz wariant', optsTitle: 'Wybierz opcje',
        subAdd: 'Wybierz opcję, aby dodać do ulubionych', subEdit: 'Zmień swój wybór',
        add: 'Dodaj do ulubionych', save: 'Zapisz', sold: 'Wyprzedane', unavail: 'Ta kombinacja jest niedostępna' };

  var readWL = function () { var v = localStorage.getItem(WL_KEY); return v ? v.split(',') : []; };
  var inWL = function (h) { return readWL().indexOf(h) !== -1; };
  var readSizes = function () { try { return JSON.parse(localStorage.getItem(SIZE_KEY) || '{}'); } catch (e) { return {}; } };
  var writeSizes = function (m) { localStorage.setItem(SIZE_KEY, JSON.stringify(m)); };
  var setSize = function (h, v) { var m = readSizes(); m[h] = v; writeSizes(m); };
  var clearSize = function (h) { var m = readSizes(); if (m[h]) { delete m[h]; writeSizes(m); } };
  var storedFor = function (h) { return readSizes()[h] || null; };

  var jsCache = {};
  var fetchJS = function (h) {
    if (jsCache[h]) return jsCache[h];
    jsCache[h] = fetch('/products/' + encodeURIComponent(h) + '.js').then(function (r) { return r.json(); });
    return jsCache[h];
  };

  var money = function (c) {
    var z = c / 100, opts = { minimumFractionDigits: (c % 100 ? 2 : 0), maximumFractionDigits: 2 };
    var s; try { s = z.toLocaleString(EN ? 'en-US' : 'pl-PL', opts); } catch (e) { s = String(z); }
    return s + ' zł';
  };

  // option name(s) -> dialog title (keeps the "size" wording for the current size products)
  var titleFor = function (names) {
    if (names.length >= 2) return T.optsTitle;
    var n = (names[0] || '').toLowerCase();
    if (/rozmiar|długo|size|length/.test(n)) return T.sizeTitle;
    return T.variantTitle;
  };

  // is there an available variant with options[idx] == value, honouring the
  // values currently selected in the OTHER option groups?
  var valueOk = function (product, idx, value, selected) {
    return (product.variants || []).some(function (v) {
      if (!v.available) return false;
      if (v.options[idx] !== value) return false;
      for (var j = 0; j < selected.length; j++) {
        if (j !== idx && selected[j] != null && v.options[j] !== selected[j]) return false;
      }
      return true;
    });
  };
  // resolve a full selection (no nulls) to its variant
  var variantFor = function (product, selected) {
    if (selected.some(function (s) { return s == null; })) return null;
    return (product.variants || []).filter(function (v) {
      return selected.every(function (s, i) { return v.options[i] === s; });
    })[0] || null;
  };

  var CSS =
    '.x-wsz-overlay{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;' +
    'background:rgba(29,29,29,.42);padding:20px;opacity:0;transition:opacity .2s ease;}' +
    '.x-wsz-overlay[hidden]{display:none;}' +
    '.x-wsz-overlay.is-open{opacity:1;}' +
    '.x-wsz-modal{position:relative;width:100%;max-width:380px;background:#FCFAF7;border:1px solid #e6e2dc;' +
    'border-radius:12px;padding:26px 24px 24px;box-shadow:0 18px 50px -12px rgba(0,0,0,.3);' +
    'transform:translateY(8px);transition:transform .22s ease;font-family:inherit;}' +
    '.x-wsz-overlay.is-open .x-wsz-modal{transform:translateY(0);}' +
    '.x-wsz-close{position:absolute;top:10px;right:12px;border:0;background:transparent;font-size:24px;' +
    'line-height:1;color:#8a8378;cursor:pointer;padding:4px;}' +
    '.x-wsz-close:hover{color:#1d1d1d;}' +
    '.x-wsz-title{font-size:19px;font-weight:500;color:#1d1d1d;margin:0 0 4px;}' +
    '.x-wsz-sub{font-size:13px;color:#6b6b6b;margin:0 0 18px;}' +
    /* flat list (single option) */
    '.x-wsz-list{display:flex;flex-direction:column;gap:8px;margin:0 0 18px;}' +
    '.x-wsz-opt{display:flex;justify-content:space-between;align-items:center;gap:12px;width:100%;' +
    'padding:13px 16px;border:1px solid #e0dad0;border-radius:8px;background:#fff;cursor:pointer;' +
    'font-family:inherit;font-size:14px;color:#1d1d1d;transition:border-color .15s ease,background .15s ease;}' +
    '.x-wsz-opt:hover:not(.is-sold){border-color:#1d1d1d;}' +
    '.x-wsz-opt.is-on{border-color:#1d1d1d;background:#1d1d1d;color:#fff;}' +
    '.x-wsz-opt.is-on .x-wsz-opt-price{color:#fff;}' +
    '.x-wsz-opt-name{font-weight:500;}' +
    '.x-wsz-opt-price{color:#6b6b6b;font-size:13.5px;}' +
    '.x-wsz-opt.is-sold{opacity:.5;cursor:not-allowed;text-decoration:line-through;}' +
    /* grouped (2+ options) */
    '.x-wsz-group{margin:0 0 16px;}' +
    '.x-wsz-glabel{font-size:12px;letter-spacing:.06em;text-transform:uppercase;color:#6b6b6b;margin:0 0 8px;}' +
    '.x-wsz-chips{display:flex;flex-wrap:wrap;gap:8px;}' +
    '.x-wsz-chip{padding:9px 14px;border:1px solid #e0dad0;border-radius:8px;background:#fff;cursor:pointer;' +
    'font-family:inherit;font-size:14px;color:#1d1d1d;transition:border-color .15s ease,background .15s ease,color .15s ease;}' +
    '.x-wsz-chip:hover:not(:disabled){border-color:#1d1d1d;}' +
    '.x-wsz-chip.is-on{border-color:#1d1d1d;background:#1d1d1d;color:#fff;}' +
    '.x-wsz-chip.is-sold{opacity:.4;cursor:not-allowed;text-decoration:line-through;}' +
    '.x-wsz-resolved{min-height:18px;margin:0 0 16px;font-size:15px;font-weight:500;color:#1d1d1d;}' +
    '.x-wsz-resolved.is-bad{color:#9a5a4a;font-weight:400;font-size:13px;}' +
    /* add button */
    '.x-wsz-add{width:100%;padding:14px;background:#1d1d1d;color:#fff;border:0;border-radius:6px;' +
    'font-family:inherit;font-size:12px;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;' +
    'transition:background .2s ease;}' +
    '.x-wsz-add:hover:not(:disabled){background:#000;}' +
    '.x-wsz-add:disabled{background:#c4bdb2;cursor:not-allowed;}';

  var overlay, titleEl, subEl, bodyEl, resolvedEl, addBtn;
  var current = null, chosen = null;

  var build = function () {
    if (overlay) return;
    var st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
    overlay = document.createElement('div');
    overlay.className = 'x-wsz-overlay'; overlay.setAttribute('hidden', '');
    overlay.innerHTML =
      '<div class="x-wsz-modal" role="dialog" aria-modal="true">' +
        '<button type="button" class="x-wsz-close" aria-label="×">×</button>' +
        '<p class="x-wsz-title"></p>' +
        '<p class="x-wsz-sub"></p>' +
        '<div class="x-wsz-body"></div>' +
        '<p class="x-wsz-resolved"></p>' +
        '<button type="button" class="x-wsz-add" disabled></button>' +
      '</div>';
    document.body.appendChild(overlay);
    titleEl = overlay.querySelector('.x-wsz-title');
    subEl = overlay.querySelector('.x-wsz-sub');
    bodyEl = overlay.querySelector('.x-wsz-body');
    resolvedEl = overlay.querySelector('.x-wsz-resolved');
    addBtn = overlay.querySelector('.x-wsz-add');
    overlay.querySelector('.x-wsz-close').addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    addBtn.addEventListener('click', confirm);
    document.addEventListener('keydown', function (e) {
      if (overlay && !overlay.hasAttribute('hidden') && e.key === 'Escape') close();
    });
  };

  function close() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    setTimeout(function () { if (overlay) overlay.setAttribute('hidden', ''); }, 200);
    current = null; chosen = null;
  }

  // --- flat list: single option (e.g. only size, or only material) ---
  function renderFlat(product, preId) {
    var list = document.createElement('div'); list.className = 'x-wsz-list';
    (product.variants || []).forEach(function (v) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'x-wsz-opt';
      if (!v.available) { b.classList.add('is-sold'); b.disabled = true; }
      b.innerHTML = '<span class="x-wsz-opt-name"></span><span class="x-wsz-opt-price"></span>';
      b.querySelector('.x-wsz-opt-name').textContent = v.title;
      b.querySelector('.x-wsz-opt-price').textContent = v.available ? money(v.price) : T.sold;
      if (v.available) {
        b.addEventListener('click', function () {
          list.querySelectorAll('.x-wsz-opt').forEach(function (o) { o.classList.remove('is-on'); });
          b.classList.add('is-on');
          chosen = { id: v.id, title: v.title, price: v.price };
          addBtn.disabled = false;
        });
        if (preId && String(v.id) === String(preId)) {
          b.classList.add('is-on');
          chosen = { id: v.id, title: v.title, price: v.price };
          addBtn.disabled = false;
        }
      }
      list.appendChild(b);
    });
    bodyEl.innerHTML = ''; bodyEl.appendChild(list);
    resolvedEl.style.display = 'none';
  }

  // --- grouped: 2+ options (e.g. size + material) ---
  function renderGrouped(product, preId) {
    var names = (product.options || []).map(function (o) { return o.name; });
    var selected = names.map(function () { return null; });
    if (preId) {
      var pv = (product.variants || []).filter(function (v) { return String(v.id) === String(preId); })[0];
      if (pv) selected = pv.options.slice();
    }
    bodyEl.innerHTML = '';
    resolvedEl.style.display = '';

    function refresh() {
      (product.options || []).forEach(function (opt, i) {
        var group = bodyEl.querySelector('[data-opt="' + i + '"]');
        group.querySelectorAll('.x-wsz-chip').forEach(function (chip) {
          var val = chip.dataset.val;
          var ok = valueOk(product, i, val, selected);
          chip.disabled = !ok;
          chip.classList.toggle('is-sold', !ok);
          if (!ok && selected[i] === val) selected[i] = null;
          chip.classList.toggle('is-on', selected[i] === val);
        });
      });
      var v = variantFor(product, selected);
      if (v && v.available) {
        chosen = { id: v.id, title: v.title, price: v.price };
        addBtn.disabled = false;
        resolvedEl.textContent = money(v.price);
        resolvedEl.classList.remove('is-bad');
      } else {
        chosen = null; addBtn.disabled = true;
        var full = selected.every(function (s) { return s != null; });
        resolvedEl.textContent = full ? T.unavail : '';
        resolvedEl.classList.toggle('is-bad', full);
      }
    }

    (product.options || []).forEach(function (opt, i) {
      var g = document.createElement('div'); g.className = 'x-wsz-group'; g.dataset.opt = i;
      var lab = document.createElement('p'); lab.className = 'x-wsz-glabel'; lab.textContent = opt.name;
      var chips = document.createElement('div'); chips.className = 'x-wsz-chips';
      (opt.values || []).forEach(function (val) {
        var c = document.createElement('button');
        c.type = 'button'; c.className = 'x-wsz-chip'; c.dataset.val = val; c.textContent = val;
        c.addEventListener('click', function () {
          if (c.disabled) return;
          selected[i] = (selected[i] === val) ? null : val;
          refresh();
        });
        chips.appendChild(c);
      });
      g.appendChild(lab); g.appendChild(chips); bodyEl.appendChild(g);
    });
    refresh();
  }

  var open = function (handle, button, product, opts) {
    opts = opts || {};
    build();
    current = { handle: handle, button: button, mode: opts.mode || 'add', onDone: opts.onDone || null };
    chosen = null;
    addBtn.disabled = true;
    var names = (product.options || []).map(function (o) { return o.name; });
    titleEl.textContent = titleFor(names);
    subEl.textContent = current.mode === 'edit' ? T.subEdit : T.subAdd;
    addBtn.textContent = current.mode === 'edit' ? T.save : T.add;
    var pre = (current.mode === 'edit') ? storedFor(handle) : null;
    var preId = pre ? pre.id : null;
    if (names.length >= 2) renderGrouped(product, preId);
    else renderFlat(product, preId);
    overlay.removeAttribute('hidden');
    // next frame -> fade/slide in
    requestAnimationFrame(function () { requestAnimationFrame(function () { overlay.classList.add('is-open'); }); });
  };

  function confirm() {
    if (!current || !chosen) return;
    setSize(current.handle, chosen);
    // capture before close() resets `chosen`/`current`
    var mode = current.mode, btn = current.button, onDone = current.onDone, picked = chosen;
    close();
    if (mode === 'edit') { if (onDone) onDone(picked); return; }
    btn.dataset.wszBypass = '1'; // let the next click reach the engine
    btn.click();                 // engine adds the handle + toggles the heart
  }

  // Public API: the favourites page "Zmień" button re-opens the picker in edit mode.
  window.SalloraWishlistPicker = {
    openEdit: function (handle, onDone) {
      fetchJS(handle).then(function (p) {
        if (!p || !p.variants || p.variants.length <= 1) return; // nothing to change
        open(handle, null, p, { mode: 'edit', onDone: onDone });
      }).catch(function () {});
    }
  };

  // Intercept ADD clicks in the capture phase, before the engine's own handler.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest(BTN) : null;
    if (!btn) return;
    if (btn.dataset.wszBypass) { delete btn.dataset.wszBypass; return; } // 2nd pass -> engine
    var handle = btn.dataset.productHandle;
    if (!handle) return;
    if (inWL(handle)) { clearSize(handle); return; } // removing -> engine removes, drop size
    // adding -> decide if an option choice is needed
    e.preventDefault();
    e.stopImmediatePropagation();
    fetchJS(handle).then(function (p) {
      if (!p || !p.variants || p.variants.length <= 1) { btn.dataset.wszBypass = '1'; btn.click(); return; }
      open(handle, btn, p, { mode: 'add' });
    }).catch(function () { btn.dataset.wszBypass = '1'; btn.click(); });
  }, true);
})();

/* ============================================================
   Sallora — restore the pre-"Kup wszystko" cart.
   Favourites buy-now clears the cart so checkout holds ONLY the favourites; before
   clearing, checkoutOnly() stashed the previous lines (minus the gift box) here.
   When the shopper RETURNS to the shop -- cancelled OR finished checkout -- SET the
   cart back to EXACTLY those lines (clear whatever the buy-now left = favourites + the
   free box, then re-add the backup) and reload, so the cart display AND the counter are
   correct and nothing of the buy-now lingers. One-shot; add-only on the restore items. */
(function () {
  'use strict';
  var RKEY = 'sallora-cart-restore';
  var raw = null;
  try { raw = localStorage.getItem(RKEY); } catch (e) { return; }
  if (!raw) return;
  try { localStorage.removeItem(RKEY); } catch (e) {} // one-shot: claim before touching the cart
  var items;
  try { items = JSON.parse(raw); } catch (e) { return; }
  if (!Array.isArray(items) || !items.length) return;

  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var H = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  function post(url, body) {
    return fetch(ROOT + url, { method: 'POST', headers: H, body: JSON.stringify(body) })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
  // re-add the backup lines one by one -> a sold-out / removed line is just skipped
  function addSeq(i, done) {
    if (i >= items.length) return done();
    post('cart/add.js', { items: [{ id: items[i].id, quantity: items[i].quantity || 1 }] })
      .then(function () { addSeq(i + 1, done); });
  }
  // clear whatever the buy-now left (favourites + free box), then restore EXACTLY the backup
  post('cart/clear.js', {}).then(function () {
    addSeq(0, function () {
      // a reload is the simplest reliable refresh of EVERY cart surface
      // (header counter, drawer AND the /cart page) after this multi-step rewrite
      try { window.location.reload(); } catch (e) {}
    });
  });
})();
